/**
 * Trang trinh dien: bam mot cai la vao thang.
 *
 * Muc dich rat hep - de nguoi trinh bay khong phai go mat khau va cho ma mot lan giua
 * buoi noi chuyen voi khach hang. Ba diem cua trang nay:
 *
 *   1. HAI CONG DANG NHAP KHAC NHAU. Nguoi choi va nhan su dung KHOA ma truy cap khac
 *      nhau trong kho cuc bo, va o hai ung dung tach roi (`/` va `/admin.html`). Bam
 *      mot tai khoan nhan su thi phai dat dung khoa cua cong quan tri roi mo tep do.
 *
 *   2. NOI RO AI THUOC THU MUC NAO. Nhan su nen tang va nhan su nha khai thac thay hai
 *      man hinh khac han, va nguoi trinh bay can biet minh dang o vai nao - do chinh la
 *      dieu can cho khach hang thay.
 *
 *   3. TRANG NAY CHI TON TAI KHI `DEMO_MODE` BAT. May chu khong bat co thi cac duong
 *      dan tra 404, va trang hien dung mot dong giai thich thay vi mot loi kho hieu.
 */

import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { request, setToken } from '../lib/api';
import { Badge, Button, Card, EmptyState, Skeleton } from '../components/ui';
import styles from './Demo.module.css';

/** Khoa ma truy cap cua cong quan tri - phai khop `web/src/admin/lib/api.ts`. */
const ADMIN_TOKEN_KEY = 'gikidi.admin.access_token';

interface DemoPlayer {
  username: string;
  full_name: string | null;
  operator: string;
  balance: string | null;
  currency: string | null;
}

interface DemoStaff {
  email: string;
  full_name: string;
  /** `null` = nhan su nen tang. */
  operator: string | null;
}

function formatBalance(balance: string | null, currency: string | null): string {
  if (balance === null || currency === null) return '—';
  return `${BigInt(balance).toLocaleString('vi-VN')} ${currency}`;
}

export function DemoPage(): JSX.Element {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const accounts = useQuery({
    queryKey: ['demo', 'accounts'],
    queryFn: () => request<{ players: DemoPlayer[]; staff: DemoStaff[] }>('/v1/demo/accounts'),
    retry: false,
  });

  async function loginPlayer(username: string): Promise<void> {
    setBusy(username);
    setError(null);
    try {
      const res = await request<{ token: string }>('/v1/demo/login/player', {
        method: 'POST', body: { username },
      });
      setToken(res.token);
      window.location.href = '/lo-de';
    } catch (err) {
      setError((err as Error).message);
      setBusy(null);
    }
  }

  async function loginStaff(email: string, operator: string | null): Promise<void> {
    setBusy(email);
    setError(null);
    try {
      const res = await request<{ token: string }>('/v1/demo/login/staff', {
        method: 'POST', body: { email },
      });
      // Cong quan tri doc ma truy cap tu khoa RIENG cua no, va chay o mot tep khac.
      // Phai la `sessionStorage` - dung kho ma `admin/lib/api.ts` doc. Ghi vao
      // `localStorage` thi cong quan tri khong thay phien va dua ve man dang nhap.
      // Chuyen trang trong cung mot the van giu `sessionStorage`.
      window.sessionStorage.setItem(ADMIN_TOKEN_KEY, res.token);
      // Nhan su nha khai thac vao man hinh cua ho; nhan su nen tang vao man hinh nen tang.
      window.location.href = operator ? '/admin.html#/lo-de' : '/admin.html#/lo-de-nen-tang';
    } catch (err) {
      setError((err as Error).message);
      setBusy(null);
    }
  }

  if (accounts.isError) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>Bản trình diễn</h1>
        <EmptyState
          title="Chế độ trình diễn đang tắt"
          description="Máy chủ chưa bật DEMO_MODE nên trang này không có tài khoản để hiển thị."
        />
      </div>
    );
  }

  const players = accounts.data?.players ?? [];
  const staff = accounts.data?.staff ?? [];
  const platformStaff = staff.filter((s) => s.operator === null);
  const operatorStaff = staff.filter((s) => s.operator !== null);

  return (
    <div className={styles.page}>
      <header>
        <h1 className={styles.title}>Bản trình diễn lô đề ba miền</h1>
        <p className={styles.lead}>
          Bấm một tài khoản để vào thẳng, không cần nhập gì. Dữ liệu là mô phỏng: hai nhà
          khai thác cùng bán trên một kỳ quay chung, kèm 15 ngày lịch sử đã quyết toán.
        </p>
      </header>

      {error && <p className={styles.error}>{error}</p>}

      <Card title="Người chơi">
        <p className={styles.note}>
          Mỗi người chơi thuộc đúng một nhà khai thác, và chỉ thấy bảng tỷ lệ của nhà khai
          thác đó. Đăng nhập hai người ở hai nhà khai thác khác nhau rồi so cùng một kỳ —
          đó là điểm đáng cho khách hàng thấy nhất.
        </p>
        {accounts.isLoading ? (
          <Skeleton height={120} />
        ) : (
          <ul className={styles.list}>
            {players.map((p) => (
              <li key={p.username} className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.name}>{p.full_name ?? p.username}</span>
                  <span className={styles.sub}>{p.operator}</span>
                </div>
                <span className={styles.balance}>{formatBalance(p.balance, p.currency)}</span>
                <Button
                  size="sm"
                  loading={busy === p.username}
                  disabled={busy !== null}
                  onClick={() => void loginPlayer(p.username)}
                >
                  Vào
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Nhân sự nền tảng">
        <p className={styles.note}>
          Vận hành kỳ quay, nộp kết quả, hủy kỳ, quản lý chuỗi sinh số. Kỳ quay là tài
          nguyên chung — không thuộc nhà khai thác nào.
        </p>
        <ul className={styles.list}>
          {platformStaff.map((s) => (
            <li key={s.email} className={styles.row}>
              <div className={styles.rowMain}>
                <span className={styles.name}>{s.full_name}</span>
                <span className={styles.sub}>{s.email}</span>
              </div>
              <Badge tone="brand">Nền tảng</Badge>
              <Button
                size="sm"
                loading={busy === s.email}
                disabled={busy !== null}
                onClick={() => void loginStaff(s.email, null)}
              >
                Vào
              </Button>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Nhân sự nhà khai thác">
        <p className={styles.note}>
          Bật đài, bật loại cược, ghi bảng tỷ lệ, xem mức rủi ro đang giữ — chỉ trong phạm
          vi nhà khai thác của mình. Gọi đường dẫn của nền tảng luôn nhận 403.
        </p>
        <ul className={styles.list}>
          {operatorStaff.map((s) => (
            <li key={s.email} className={styles.row}>
              <div className={styles.rowMain}>
                <span className={styles.name}>{s.full_name}</span>
                <span className={styles.sub}>{s.email}</span>
              </div>
              <span className={styles.sub}>{s.operator}</span>
              <Button
                size="sm"
                loading={busy === s.email}
                disabled={busy !== null}
                onClick={() => void loginStaff(s.email, s.operator)}
              >
                Vào
              </Button>
            </li>
          ))}
        </ul>
      </Card>

      <p className={styles.footer}>
        Tài khoản nội bộ vẫn đi qua đủ hai lớp xác thực — trang này chỉ tự sinh mã một lần
        thay cho người dùng, không tắt lớp nào.
      </p>
    </div>
  );
}
